import React, { useCallback, useEffect, useState } from 'react';
import {
	Box,
	Button,
	ButtonGroup,
	CircularProgress,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import RefreshRoundedIcon        from '@mui/icons-material/RefreshRounded';
import AccessTimeRoundedIcon     from '@mui/icons-material/AccessTimeRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import CallRoundedIcon           from '@mui/icons-material/CallRounded';
import CheckCircleRoundedIcon    from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon         from '@mui/icons-material/CancelRounded';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import adminQueueService from '../../../../../services/adminQueueService';

// ─── Цвета ───────────────────────────────────────────────────────────────────
const PALETTE = {
	waiting:   { bg: '#fff8e1', border: '#f59e0b', text: '#b45309', icon: '#f59e0b' },
	called:    { bg: '#e0f2fe', border: '#38bdf8', text: '#0369a1', icon: '#38bdf8' },
	done:      { bg: '#dcfce7', border: '#4ade80', text: '#15803d', icon: '#4ade80' },
	cancelled: { bg: '#f3f4f6', border: '#9ca3af', text: '#6b7280', icon: '#9ca3af' },
	total:     { bg: '#eff6ff', border: '#3b82f6', text: '#1d4ed8', icon: '#3b82f6' },
};

// ─── Утилиты для дат ─────────────────────────────────────────────────────────
const toIso = (d) => d.toISOString().slice(0, 10);
const today     = () => toIso(new Date());
const daysAgo   = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return toIso(d); };
const yesterday = () => daysAgo(1);

// ─── Карточка ────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, palette }) {
	return (
		<StatCardRoot sx={{ background: palette.bg, borderColor: palette.border }}>
			<Box sx={{ color: palette.icon, mb: 1 }}>{icon}</Box>
			<Typography sx={{ fontSize: 36, fontWeight: 900, color: palette.text, lineHeight: 1 }}>
				{value}
			</Typography>
			<Typography sx={{ fontSize: 13, color: palette.text, opacity: 0.8, mt: 0.5, fontWeight: 600 }}>
				{label}
			</Typography>
		</StatCardRoot>
	);
}

// ─── Горизонтальный бар-чарт ─────────────────────────────────────────────────
function BarChart({ data, color = '#3b82f6', emptyText }) {
	if (!data || data.length === 0) {
		return (
			<Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
				<Typography variant='body2'>{emptyText}</Typography>
			</Box>
		);
	}
	const max = Math.max(...data.map(d => d.count), 1);
	return (
		<Stack spacing={1.5}>
			{data.map((d, i) => (
				<Box key={i}>
					<Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 0.4 }}>
						<Typography sx={{ fontSize: 13, fontWeight: 600, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
							{d.name}
						</Typography>
						<Typography sx={{ fontSize: 13, fontWeight: 700, color, ml: 1 }}>{d.count}</Typography>
					</Stack>
					<Box sx={{ height: 10, borderRadius: 99, background: alpha(color, 0.12), overflow: 'hidden' }}>
						<Box sx={{
							height: '100%',
							width: `${(d.count / max) * 100}%`,
							background: color,
							borderRadius: 99,
							transition: 'width 0.6s ease',
							minWidth: d.count > 0 ? 6 : 0,
						}} />
					</Box>
				</Box>
			))}
		</Stack>
	);
}

// ─── График по часам ─────────────────────────────────────────────────────────
function HourChart({ data }) {
	const WORK_HOURS = Array.from({ length: 14 }, (_, i) => i + 7);
	const byHour = Object.fromEntries((data || []).map(d => [d.hour, d.count]));
	const max = Math.max(...WORK_HOURS.map(h => byHour[h] || 0), 1);
	return (
		<Box>
			<Stack direction='row' alignItems='flex-end' spacing={0.5} sx={{ height: 80 }}>
				{WORK_HOURS.map(h => {
					const cnt = byHour[h] || 0;
					const pct = (cnt / max) * 100;
					return (
						<Tooltip key={h} title={`${h}:00 — ${cnt}`} arrow>
							<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'default' }}>
								<Box sx={{
									width: '100%',
									height: `${Math.max(pct, cnt > 0 ? 6 : 0)}%`,
									minHeight: cnt > 0 ? 4 : 0,
									background: cnt > 0 ? '#3b82f6' : 'rgba(0,0,0,0.08)',
									borderRadius: '4px 4px 0 0',
									transition: 'height 0.5s ease',
								}} />
							</Box>
						</Tooltip>
					);
				})}
			</Stack>
			<Stack direction='row' spacing={0.5} sx={{ mt: 0.5 }}>
				{WORK_HOURS.map(h => (
					<Box key={h} sx={{ flex: 1, textAlign: 'center' }}>
						<Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{h}</Typography>
					</Box>
				))}
			</Stack>
		</Box>
	);
}

// ─── Главный компонент ────────────────────────────────────────────────────────
function QueueMonitoring() {
	const { t } = useTranslation();

	const [dateFrom, setDateFrom] = useState(today());
	const [dateTo,   setDateTo]   = useState(today());
	const [stats, setStats]       = useState(null);
	const [loading, setLoading]   = useState(false);
	const [lastUpdated, setLastUpdated] = useState(null);

	const fetchStats = useCallback(async (from = dateFrom, to = dateTo) => {
		setLoading(true);
		try {
			const res = await adminQueueService.getTicketStats({ dateFrom: from, dateTo: to });
			setStats(res.data);
			setLastUpdated(new Date());
		} catch {
			toast.error(t('admin.monitoring.loadError'));
		} finally {
			setLoading(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dateFrom, dateTo]);

	useEffect(() => { fetchStats(dateFrom, dateTo); }, []); // eslint-disable-line react-hooks/exhaustive-deps

	const applyPreset = (from, to) => {
		setDateFrom(from);
		setDateTo(to);
		fetchStats(from, to);
	};

	const handleApply = () => fetchStats(dateFrom, dateTo);

	const activePreset =
		dateFrom === today()     && dateTo === today()     ? 'today'     :
		dateFrom === yesterday() && dateTo === yesterday() ? 'yesterday' :
		dateFrom === daysAgo(7)  && dateTo === today()     ? 'week'      :
		dateFrom === daysAgo(30) && dateTo === today()     ? 'month'     : null;

	return (
		<Box>
			{/* ── Заголовок ──────────────────────────────────────────── */}
			<Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 3 }} flexWrap='wrap' gap={1}>
				<Box>
					<Typography variant='h6' sx={{ fontWeight: 700 }}>{t('admin.monitoring.title')}</Typography>
					{lastUpdated && (
						<Typography variant='caption' sx={{ color: 'text.secondary' }}>
							{t('admin.monitoring.updatedAt', { time: lastUpdated.toLocaleTimeString('ru-RU') })}
						</Typography>
					)}
				</Box>
				<Button
					variant='outlined'
					startIcon={loading ? <CircularProgress size={16} /> : <RefreshRoundedIcon />}
					onClick={handleApply}
					disabled={loading}
					sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
				>
					{t('admin.monitoring.refresh')}
				</Button>
			</Stack>

			{/* ── Фильтр по датам ─────────────────────────────────────── */}
			<Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid rgba(0,0,0,0.08)' }}>
				<Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
					{t('admin.monitoring.periodSection')}
				</Typography>
				<Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap' useFlexGap>
					{/* Быстрые пресеты */}
					<ButtonGroup size='small' variant='outlined'>
						{[
							{ key: 'today',     label: t('admin.monitoring.presetToday'),     from: today(),     to: today() },
							{ key: 'yesterday', label: t('admin.monitoring.presetYesterday'), from: yesterday(), to: yesterday() },
							{ key: 'week',      label: t('admin.monitoring.presetWeek'),      from: daysAgo(7),  to: today() },
							{ key: 'month',     label: t('admin.monitoring.presetMonth'),     from: daysAgo(30), to: today() },
						].map(p => (
							<Button
								key={p.key}
								onClick={() => applyPreset(p.from, p.to)}
								variant={activePreset === p.key ? 'contained' : 'outlined'}
								sx={{ textTransform: 'none', fontWeight: activePreset === p.key ? 700 : 500 }}
							>
								{p.label}
							</Button>
						))}
					</ButtonGroup>

					{/* Произвольный диапазон */}
					<Stack direction='row' spacing={1} alignItems='center'>
						<TextField
							label={t('admin.monitoring.dateFrom')}
							type='date'
							size='small'
							value={dateFrom}
							onChange={e => setDateFrom(e.target.value)}
							InputLabelProps={{ shrink: true }}
							sx={{ width: 155 }}
						/>
						<Typography sx={{ color: 'text.secondary', fontSize: 14 }}>—</Typography>
						<TextField
							label={t('admin.monitoring.dateTo')}
							type='date'
							size='small'
							value={dateTo}
							onChange={e => setDateTo(e.target.value)}
							inputProps={{ min: dateFrom }}
							InputLabelProps={{ shrink: true }}
							sx={{ width: 155 }}
						/>
						<Button
							variant='contained'
							size='small'
							onClick={handleApply}
							disabled={loading || !dateFrom || !dateTo}
							sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 2.5 }}
						>
							{loading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : t('admin.monitoring.refresh')}
						</Button>
					</Stack>
				</Stack>
			</Paper>

			{loading && !stats ? (
				<Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
					<CircularProgress />
				</Box>
			) : (
				<Stack spacing={3}>
					{/* ── Карточки ───────────────────────────────────────── */}
					<Box>
						<SectionLabel>{t('admin.monitoring.todaySection')}</SectionLabel>
						<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 2, mt: 1.5 }}>
							<StatCard icon={<AccessTimeRoundedIcon sx={{ fontSize: 28 }} />}     label={t('admin.monitoring.totalToday')} value={stats?.totalToday ?? 0}    palette={PALETTE.total} />
							<StatCard icon={<HourglassEmptyRoundedIcon sx={{ fontSize: 28 }} />} label={t('admin.monitoring.waiting')}    value={stats?.waitingCount ?? 0}   palette={PALETTE.waiting} />
							<StatCard icon={<CallRoundedIcon sx={{ fontSize: 28 }} />}            label={t('admin.monitoring.called')}     value={stats?.calledCount ?? 0}    palette={PALETTE.called} />
							<StatCard icon={<CheckCircleRoundedIcon sx={{ fontSize: 28 }} />}    label={t('admin.monitoring.done')}       value={stats?.doneCount ?? 0}      palette={PALETTE.done} />
							<StatCard icon={<CancelRoundedIcon sx={{ fontSize: 28 }} />}         label={t('admin.monitoring.cancelled')}  value={stats?.cancelledCount ?? 0} palette={PALETTE.cancelled} />
						</Box>
					</Box>

					{/* ── По часам ───────────────────────────────────────── */}
					<ChartCard>
						<SectionLabel sx={{ mb: 2 }}>{t('admin.monitoring.byHour')}</SectionLabel>
						<HourChart data={stats?.byHour} />
					</ChartCard>

					<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
						{/* ── По типу услуги ──────────────────────────────── */}
						<ChartCard>
							<SectionLabel sx={{ mb: 2 }}>{t('admin.monitoring.byServiceType')}</SectionLabel>
							<BarChart
								data={stats?.byServiceType}
								color='#8b5cf6'
								emptyText={t('admin.monitoring.noServiceTypeData')}
							/>
						</ChartCard>

						{/* ── По менеджерам ───────────────────────────────── */}
						<ChartCard>
							<SectionLabel sx={{ mb: 2 }}>{t('admin.monitoring.byManager')}</SectionLabel>
							{stats?.byManager?.length ? (
								<TableContainer>
									<Table size='small'>
										<TableHead>
											<TableRow>
												<TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('admin.monitoring.managerColName')}</TableCell>
												<TableCell sx={{ fontWeight: 700, fontSize: 12, textAlign: 'right' }}>{t('admin.monitoring.managerColCount')}</TableCell>
												<TableCell sx={{ fontWeight: 700, fontSize: 12, textAlign: 'right', width: 120 }}>{t('admin.monitoring.managerColShare')}</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{stats.byManager.map((m, i) => {
												const total = stats.byManager.reduce((s, x) => s + x.count, 0) || 1;
												const pct   = Math.round((m.count / total) * 100);
												return (
													<TableRow key={i} sx={{ '&:last-child td': { borderBottom: 0 } }}>
														<TableCell sx={{ fontSize: 13 }}>{m.name}</TableCell>
														<TableCell sx={{ textAlign: 'right', fontWeight: 700, fontSize: 13 }}>{m.count}</TableCell>
														<TableCell sx={{ textAlign: 'right' }}>
															<Stack direction='row' alignItems='center' spacing={1} justifyContent='flex-end'>
																<Box sx={{ width: 60, height: 6, borderRadius: 99, background: 'rgba(0,0,0,0.08)', overflow: 'hidden' }}>
																	<Box sx={{ width: `${pct}%`, height: '100%', background: '#3b82f6', borderRadius: 99 }} />
																</Box>
																<Typography sx={{ fontSize: 12, color: 'text.secondary', minWidth: 30 }}>{pct}%</Typography>
															</Stack>
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</TableContainer>
							) : (
								<Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
									<Typography variant='body2'>{t('admin.monitoring.noManagerData')}</Typography>
								</Box>
							)}
						</ChartCard>
					</Box>
				</Stack>
			)}
		</Box>
	);
}

// ─── Styled ───────────────────────────────────────────────────────────────────

const StatCardRoot = styled(Paper)(() => ({
	padding: '20px 16px',
	borderRadius: 16,
	border: '1.5px solid',
	boxShadow: 'none',
	textAlign: 'center',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
}));

const ChartCard = styled(Paper)(() => ({
	padding: '20px 24px',
	borderRadius: 16,
	border: '1px solid rgba(0,0,0,0.08)',
	boxShadow: 'none',
}));

const SectionLabel = styled(Typography)(() => ({
	fontSize: 14,
	fontWeight: 700,
	color: '#374151',
	textTransform: 'uppercase',
	letterSpacing: '0.06em',
}));

export default QueueMonitoring;
